import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Check, Sparkle, XCircle } from "@phosphor-icons/react";

const FREE = [
  "Framework Explorer (3 frameworks, all sections)",
  "9-step Third-Party Assessment guide",
  "Interview Q&A bank (Easy questions)",
  "1 quiz preview",
];
const PRO = [
  "Everything in Free",
  "AI Tutor — unlimited Claude Sonnet chat",
  "All Interview Q&A (Medium & Hard)",
  "All interactive quizzes with scoring",
  "Downloadable checklists (.md)",
  "Progress tracking across devices",
];

export default function Pricing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => { api.get("/billing/config").then((r) => setCfg(r.data)); }, []);

  const activate = async (subscriptionID) => {
    try {
      await api.post("/billing/activate", { subscription_id: subscriptionID });
      toast.success("Welcome to Pro! Refreshing…");
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Activation failed");
    }
  };

  const cancel = async () => {
    if (!confirm("Cancel your Pro subscription?")) return;
    try {
      await api.post("/billing/cancel");
      toast.success("Subscription cancelled");
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    }
  };

  return (
    <div className="space-y-10">
      <header className="fade-up">
        <div className="overline mb-3">Pricing</div>
        <h1 className="font-display text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
          Go Pro. Own the interview.
        </h1>
        <p className="text-muted-foreground mt-4 max-w-2xl">
          Free is enough to explore. Pro unlocks the AI Tutor, every quiz, every Q&A, and every checklist.
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-t border-l border-border">
        {/* FREE */}
        <div className="p-8 bg-white border-r border-b border-border" data-testid="plan-free">
          <div className="overline">Free</div>
          <div className="font-display text-5xl mt-3 leading-none">$0</div>
          <div className="text-sm text-muted-foreground mt-1">Forever</div>
          <ul className="mt-8 space-y-2 text-sm">
            {FREE.map((f) => (
              <li key={f} className="flex gap-2"><Check size={16} weight="bold" className="text-primary shrink-0 mt-0.5" />{f}</li>
            ))}
          </ul>
          <button disabled className="mt-8 w-full py-3 text-sm border border-border text-muted-foreground" data-testid="plan-free-cta">
            Your current plan
          </button>
        </div>

        {/* PRO */}
        <div className="p-8 bg-foreground text-background border-r border-b border-border relative" data-testid="plan-pro">
          <div className="absolute top-4 right-4 text-[10px] font-mono uppercase tracking-widest bg-accent text-white px-2 py-1">Most popular</div>
          <div className="overline text-accent">Pro</div>
          <div className="font-display text-5xl mt-3 leading-none">${cfg?.price || "19"}<span className="text-xl text-white/60">/mo</span></div>
          <div className="text-sm text-white/60 mt-1">Billed monthly · Cancel anytime</div>
          <ul className="mt-8 space-y-2 text-sm">
            {PRO.map((f) => (
              <li key={f} className="flex gap-2"><Sparkle size={16} weight="fill" className="text-accent shrink-0 mt-0.5" />{f}</li>
            ))}
          </ul>

          <div className="mt-8">
            {user?.is_pro ? (
              <button onClick={cancel}
                data-testid="cancel-pro"
                className="w-full py-3 text-sm border border-white/40 hover:bg-accent hover:border-accent transition-colors flex items-center justify-center gap-2">
                <XCircle size={14} /> Cancel subscription
              </button>
            ) : cfg ? (
              <div className="bg-white p-3" data-testid="paypal-buttons-wrap" key={refresh}>
                <PayPalScriptProvider options={{
                  clientId: cfg.client_id,
                  vault: true,
                  intent: "subscription",
                  currency: cfg.currency,
                }}>
                  <PayPalButtons
                    style={{ layout: "vertical", label: "subscribe", shape: "rect" }}
                    createSubscription={(data, actions) =>
                      actions.subscription.create({ plan_id: cfg.plan_id })
                    }
                    onApprove={(data) => activate(data.subscriptionID)}
                    onError={() => toast.error("PayPal error — please try again")}
                    onCancel={() => toast.info("Checkout cancelled")}
                  />
                </PayPalScriptProvider>
              </div>
            ) : (
              <div className="text-xs text-white/60">Loading PayPal…</div>
            )}
          </div>
        </div>
      </div>

      {user?.is_pro && (
        <div className="border border-primary bg-primary/5 p-4 text-sm" data-testid="pro-status">
          You're on Pro. Subscription ID: <code className="font-mono">{user.subscription_id}</code>
        </div>
      )}
    </div>
  );
}
